// Queue registry — the single source of truth for the sell-request lifecycle
// surface. One row per sidebar entry / page / route.
//
// Sidebar items, route records, page files, badge counts, the KPI row, and the
// detail page's "back to my queue" all read from this table, so adding a
// lifecycle status is ONE row here plus a page file — never a nine-site edit
// (coding-principles §9.4 OCP).
//
// Status → queue mapping follows the admin-service state machine
// (`listing-request-transitions.ts`). The four work queues are the four stages,
// in order: Review → Owner Onboarding → Agreements → Final Approval.
//
// Four statuses deliberately get no queue — `draft` is seller-side and never
// submitted, and the other three are retired in the BE enum and never entered:
//   - `under_review`       — admins act directly on `submitted`
//   - `approved`           — a milestone stamped as `approved_at`, not a resting
//                            state; approve advances straight to owner onboarding
//   - `owner_verification` — owner verification is part of review. The reviewer
//                            verifies every declared owner from the Review queue
//                            and then approves, in one sitting.

import type { PiIconName } from '@uiKit/elements/PiIcon.vue'
import { routes } from '@app/router/routes'
import { LISTING_REQUEST_STATUS } from './listingRequestStatus'
import type { ListingRequestStatus } from '../types/listingRequestsTypes'

/** Queue identifiers. A page file passes one of these to the queue container. */
export enum LISTING_REQUEST_QUEUE {
  REVIEW = 'review',
  OWNERS_ONBOARDING = 'ownersOnboarding',
  AGREEMENTS = 'agreements',
  FINAL_APPROVAL = 'finalApproval',
  REVISION_REQUESTED = 'revisionRequested',
  ALL = 'all',
  PROMOTED = 'promoted',
  REJECTED = 'rejected',
  WITHDRAWN = 'withdrawn',
}

export type ListingRequestQueueKey = `${LISTING_REQUEST_QUEUE}`

/**
 * Which action set the record page offers. Maps a queue to the lifecycle stage
 * the operator is acting on, so the record page mounts that stage's action
 * container instead of stacking every stage.
 */
export enum LISTING_REQUEST_STAGE {
  /** Review the property, settle the price, verify the declared owners, then approve. */
  REVIEW = 'review',
  /** The declared-owner checklist, then "start agreement signing". */
  OWNERS_ONBOARDING = 'ownersOnboarding',
  AGREEMENT = 'agreement',
  /** The last click — creates the draft property and closes the request. */
  FINAL_APPROVAL = 'finalApproval',
  /** Terminal or seller-side — the record is readable, not actionable. */
  READ_ONLY = 'readOnly',
}

/** Sidebar grouping. Order here is the order the groups render. */
export enum LISTING_REQUEST_NAV_GROUP {
  WORK_QUEUES = 'Work queues',
  AWAITING_SELLER = 'Awaiting seller',
  DIRECTORY = 'Directory',
}

export interface ListingRequestQueueConfig {
  key: ListingRequestQueueKey
  /** Route path constant — also the sidebar item's `to`. */
  route: string
  /** vue-router record name. */
  routeName: string
  /** Page heading + sidebar label. */
  title: string
  /** Shorter sidebar label when the page title is too long for the nav. */
  navLabel: string
  icon: PiIconName
  navGroup: LISTING_REQUEST_NAV_GROUP
  /**
   * Statuses the queue lists. Empty = no status filter (the All Requests
   * directory view). Sent to the BE as a repeated `status` param — the list
   * endpoint accepts an array or a comma-separated string.
   */
  statuses: ListingRequestStatus[]
  stage: LISTING_REQUEST_STAGE
  /** Pluralised noun for the "N <noun>" header subtitle. */
  countNoun: string
  /**
   * Show the STATUS column. Only the All Requests directory shows it — each work
   * queue already names its stage in the page title, so the column is dead width.
   */
  showStatusColumn: boolean
  /** Render the pipeline KPI row above the table. Work queues only. */
  showPipelineKpis: boolean
  emptyTitle: string
  emptySubtitle: string
}

export const LISTING_REQUEST_QUEUES: Record<ListingRequestQueueKey, ListingRequestQueueConfig> = {
  [LISTING_REQUEST_QUEUE.REVIEW]: {
    key: LISTING_REQUEST_QUEUE.REVIEW,
    route: routes.LISTING_REQUESTS_REVIEW,
    routeName: 'LISTING_REQUESTS_REVIEW',
    title: 'Review Requests',
    navLabel: 'Review Requests',
    icon: 'inbox',
    navGroup: LISTING_REQUEST_NAV_GROUP.WORK_QUEUES,
    statuses: [LISTING_REQUEST_STATUS.SUBMITTED],
    stage: LISTING_REQUEST_STAGE.REVIEW,
    countNoun: 'awaiting review',
    showStatusColumn: false,
    showPipelineKpis: true,
    emptyTitle: 'No requests awaiting review',
    emptySubtitle: 'Newly submitted sell requests land here.',
  },
  [LISTING_REQUEST_QUEUE.OWNERS_ONBOARDING]: {
    key: LISTING_REQUEST_QUEUE.OWNERS_ONBOARDING,
    route: routes.LISTING_REQUESTS_OWNERS_ONBOARDING,
    routeName: 'LISTING_REQUESTS_OWNERS_ONBOARDING',
    title: 'Owner Onboarding',
    navLabel: 'Owner Onboarding',
    icon: 'users',
    navGroup: LISTING_REQUEST_NAV_GROUP.WORK_QUEUES,
    statuses: [LISTING_REQUEST_STATUS.OWNERS_ONBOARDING],
    stage: LISTING_REQUEST_STAGE.OWNERS_ONBOARDING,
    countNoun: 'onboarding owners',
    showStatusColumn: false,
    showPipelineKpis: true,
    emptyTitle: 'Nothing in owner onboarding',
    emptySubtitle: 'Accepted requests appear here while their owners onboard.',
  },
  [LISTING_REQUEST_QUEUE.AGREEMENTS]: {
    key: LISTING_REQUEST_QUEUE.AGREEMENTS,
    route: routes.LISTING_REQUESTS_AGREEMENTS,
    routeName: 'LISTING_REQUESTS_AGREEMENTS',
    title: 'Agreements',
    navLabel: 'Agreements',
    icon: 'file-edit',
    navGroup: LISTING_REQUEST_NAV_GROUP.WORK_QUEUES,
    // `agreement_rejected` sits here rather than under Rejected: the agreement
    // was declined, not the listing — the operator re-uploads from this queue.
    statuses: [
      LISTING_REQUEST_STATUS.AGREEMENT_IN_PROGRESS,
      LISTING_REQUEST_STATUS.AGREEMENT_REJECTED,
    ],
    stage: LISTING_REQUEST_STAGE.AGREEMENT,
    countNoun: 'in agreement',
    showStatusColumn: false,
    showPipelineKpis: true,
    emptyTitle: 'No agreements in progress',
    emptySubtitle:
      'Requests whose owners have all onboarded appear here for the MOU and Seller Agreement.',
  },
  [LISTING_REQUEST_QUEUE.FINAL_APPROVAL]: {
    key: LISTING_REQUEST_QUEUE.FINAL_APPROVAL,
    route: routes.LISTING_REQUESTS_FINAL_APPROVAL,
    routeName: 'LISTING_REQUESTS_FINAL_APPROVAL',
    title: 'Final Approval',
    navLabel: 'Final Approval',
    icon: 'verified',
    navGroup: LISTING_REQUEST_NAV_GROUP.WORK_QUEUES,
    statuses: [LISTING_REQUEST_STATUS.AGREEMENT_COMPLETED],
    stage: LISTING_REQUEST_STAGE.FINAL_APPROVAL,
    countNoun: 'awaiting final approval',
    showStatusColumn: false,
    showPipelineKpis: true,
    emptyTitle: 'Nothing awaiting final approval',
    emptySubtitle: 'Requests whose agreements are fully signed and approved appear here.',
  },
  [LISTING_REQUEST_QUEUE.REVISION_REQUESTED]: {
    key: LISTING_REQUEST_QUEUE.REVISION_REQUESTED,
    route: routes.LISTING_REQUESTS_REVISION_REQUESTED,
    routeName: 'LISTING_REQUESTS_REVISION_REQUESTED',
    title: 'Revision Requested',
    navLabel: 'Revision Requested',
    icon: 'undo',
    navGroup: LISTING_REQUEST_NAV_GROUP.AWAITING_SELLER,
    statuses: [LISTING_REQUEST_STATUS.REVISION_REQUESTED],
    stage: LISTING_REQUEST_STAGE.READ_ONLY,
    countNoun: 'awaiting seller',
    showStatusColumn: false,
    showPipelineKpis: false,
    emptyTitle: 'Nothing awaiting a seller',
    emptySubtitle: 'Requests sent back for revision appear here until resubmitted.',
  },
  [LISTING_REQUEST_QUEUE.ALL]: {
    key: LISTING_REQUEST_QUEUE.ALL,
    route: routes.LISTING_REQUESTS_ALL,
    routeName: 'LISTING_REQUESTS_ALL',
    title: 'All Requests',
    navLabel: 'All Requests',
    icon: 'bars',
    navGroup: LISTING_REQUEST_NAV_GROUP.DIRECTORY,
    statuses: [],
    stage: LISTING_REQUEST_STAGE.READ_ONLY,
    countNoun: 'requests',
    showStatusColumn: true,
    showPipelineKpis: false,
    emptyTitle: 'No sell requests',
    emptySubtitle: 'Sell requests appear here once submitted.',
  },
  [LISTING_REQUEST_QUEUE.PROMOTED]: {
    key: LISTING_REQUEST_QUEUE.PROMOTED,
    route: routes.LISTING_REQUESTS_PROMOTED,
    routeName: 'LISTING_REQUESTS_PROMOTED',
    title: 'Approved for Property Setup',
    navLabel: 'Approved for Property Setup',
    icon: 'check-circle',
    navGroup: LISTING_REQUEST_NAV_GROUP.DIRECTORY,
    statuses: [LISTING_REQUEST_STATUS.PROMOTED],
    stage: LISTING_REQUEST_STAGE.READ_ONLY,
    countNoun: 'approved for property setup',
    showStatusColumn: false,
    showPipelineKpis: false,
    emptyTitle: 'Nothing approved for property setup yet',
    emptySubtitle:
      'Requests given final approval appear here — their properties continue in Properties.',
  },
  [LISTING_REQUEST_QUEUE.REJECTED]: {
    key: LISTING_REQUEST_QUEUE.REJECTED,
    route: routes.LISTING_REQUESTS_REJECTED,
    routeName: 'LISTING_REQUESTS_REJECTED',
    title: 'Rejected',
    navLabel: 'Rejected',
    icon: 'ban',
    navGroup: LISTING_REQUEST_NAV_GROUP.DIRECTORY,
    statuses: [LISTING_REQUEST_STATUS.REJECTED],
    stage: LISTING_REQUEST_STAGE.READ_ONLY,
    countNoun: 'rejected',
    showStatusColumn: false,
    showPipelineKpis: false,
    emptyTitle: 'No rejected requests',
    emptySubtitle: 'Declined sell requests appear here with their reason.',
  },
  [LISTING_REQUEST_QUEUE.WITHDRAWN]: {
    key: LISTING_REQUEST_QUEUE.WITHDRAWN,
    route: routes.LISTING_REQUESTS_WITHDRAWN,
    routeName: 'LISTING_REQUESTS_WITHDRAWN',
    title: 'Withdrawn',
    navLabel: 'Withdrawn',
    icon: 'times-circle',
    navGroup: LISTING_REQUEST_NAV_GROUP.DIRECTORY,
    statuses: [LISTING_REQUEST_STATUS.WITHDRAWN],
    stage: LISTING_REQUEST_STAGE.READ_ONLY,
    countNoun: 'withdrawn',
    showStatusColumn: false,
    showPipelineKpis: false,
    emptyTitle: 'No withdrawn requests',
    emptySubtitle: 'Requests the seller pulled back appear here.',
  },
}

/** Nav render order — sidebar groups iterate this, not `Object.values`. */
export const LISTING_REQUEST_NAV_ORDER: ListingRequestQueueKey[] = [
  LISTING_REQUEST_QUEUE.REVIEW,
  LISTING_REQUEST_QUEUE.OWNERS_ONBOARDING,
  LISTING_REQUEST_QUEUE.AGREEMENTS,
  LISTING_REQUEST_QUEUE.FINAL_APPROVAL,
  LISTING_REQUEST_QUEUE.REVISION_REQUESTED,
  LISTING_REQUEST_QUEUE.ALL,
  LISTING_REQUEST_QUEUE.PROMOTED,
  LISTING_REQUEST_QUEUE.REJECTED,
  LISTING_REQUEST_QUEUE.WITHDRAWN,
]

/** The four work queues, in pipeline order — drives the KPI row. */
export const LISTING_REQUEST_PIPELINE_QUEUES: ListingRequestQueueKey[] = [
  LISTING_REQUEST_QUEUE.REVIEW,
  LISTING_REQUEST_QUEUE.OWNERS_ONBOARDING,
  LISTING_REQUEST_QUEUE.AGREEMENTS,
  LISTING_REQUEST_QUEUE.FINAL_APPROVAL,
]

export function getListingRequestQueue(key: ListingRequestQueueKey): ListingRequestQueueConfig {
  return LISTING_REQUEST_QUEUES[key]
}

/**
 * Narrows an arbitrary string (a URL param) to a queue key. The record page
 * carries `?from=<queueKey>` so "back" returns the operator to the queue they
 * actually drilled in from — including All Requests, which `findQueueForStatus`
 * can never infer from a status alone.
 */
export function isListingRequestQueueKey(value: string): value is ListingRequestQueueKey {
  return Object.prototype.hasOwnProperty.call(LISTING_REQUEST_QUEUES, value)
}

/**
 * The queue a request currently belongs to — used by the detail page to send
 * the operator back to the queue that owns this status (not a generic list),
 * and to pick the stage container to mount. Falls back to the All Requests
 * directory for statuses with no queue (`draft`, `under_review`, `approved`,
 * `owner_verification`).
 */
export function findQueueForStatus(status: ListingRequestStatus): ListingRequestQueueConfig {
  const match = LISTING_REQUEST_NAV_ORDER.map((key) => LISTING_REQUEST_QUEUES[key]).find((queue) =>
    queue.statuses.includes(status),
  )
  return match ?? LISTING_REQUEST_QUEUES[LISTING_REQUEST_QUEUE.ALL]
}

/** Lifecycle stage for a status — drives which action set the drill-in offers. */
export function getStageForStatus(status: ListingRequestStatus): LISTING_REQUEST_STAGE {
  return findQueueForStatus(status).stage
}
